import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (amountInCents, currency = 'usd') => {
  if (amountInCents === null || amountInCents === undefined) return 'N/A';
  const normalized = amountInCents / 100;
  if (currency.toUpperCase() === 'USD') {
    return currencyFormatter.format(normalized);
  }
  return `${normalized.toFixed(2)} ${currency.toUpperCase()}`;
};

const ProductsManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [priceCounts, setPriceCounts] = useState({});
  const [error, setError] = useState('');
  const [stripeStatus, setStripeStatus] = useState('idle');
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unitAmount: '',
    currency: 'usd',
    interval: 'month',
    intervalCount: '1',
    productType: 'seat-pack',
  });

  // Product categorization state
  const [categories, setCategories] = useState({
    platformProductIds: [],
    seatPackProductIds: [],
    firstFiveProductId: null,
    archivedProductIds: [],
    displayOrder: {},
    seatCounts: {},  // { [productId]: numberOfLicenses }
  });
  const [categoriesDirty, setCategoriesDirty] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Load product categories from Firestore
  const loadCategories = useCallback(async () => {
    try {
      const docRef = doc(db, 'appConfig', 'subscriptionProducts');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setCategories({
          platformProductIds: data.platformProductIds || [],
          seatPackProductIds: data.seatPackProductIds || [],
          firstFiveProductId: data.firstFiveProductId || null,
          archivedProductIds: data.archivedProductIds || [],
          displayOrder: data.displayOrder || {},
          seatCounts: data.seatCounts || {},
        });
      }
    } catch (err) {
      console.error('Error loading product categories:', err);
    }
  }, []);

  // Save product categories to Firestore
  const saveCategories = async () => {
    setSavingCategories(true);
    try {
      const docRef = doc(db, 'appConfig', 'subscriptionProducts');
      await setDoc(docRef, {
        ...categories,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCategoriesDirty(false);
      alert('Product categories saved successfully!');
    } catch (err) {
      console.error('Error saving product categories:', err);
      alert('Error saving categories: ' + err.message);
    } finally {
      setSavingCategories(false);
    }
  };

  // Get category label for a product
  const getProductCategory = (productId) => {
    if (categories.firstFiveProductId === productId) return 'first-five';
    if (categories.platformProductIds.includes(productId)) return 'platform';
    if (categories.seatPackProductIds.includes(productId)) return 'seat-pack';
    if (categories.archivedProductIds.includes(productId)) return 'archived';
    return 'uncategorized';
  };

  // Sync existing orgs to match configured seat counts
  const syncExistingOrgs = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const functions = getFunctions();
      const syncFn = httpsCallable(functions, 'syncOrganizationLicenseCounts');
      const result = await syncFn({});
      setSyncResult(result.data);
    } catch (err) {
      console.error('Error syncing organizations:', err);
      setSyncResult({ error: err.message || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  // Set seat count for a seat pack product
  const setSeatCount = (productId, count) => {
    setCategories(prev => ({
      ...prev,
      seatCounts: {
        ...prev.seatCounts,
        [productId]: count ? parseInt(count, 10) : 0,
      },
    }));
    setCategoriesDirty(true);
  };

  // Set category for a product
  const setProductCategory = (productId, category) => {
    setCategories(prev => {
      const next = {
        platformProductIds: prev.platformProductIds.filter(id => id !== productId),
        seatPackProductIds: prev.seatPackProductIds.filter(id => id !== productId),
        archivedProductIds: prev.archivedProductIds.filter(id => id !== productId),
        firstFiveProductId: prev.firstFiveProductId === productId ? null : prev.firstFiveProductId,
        displayOrder: { ...prev.displayOrder },
      };

      switch (category) {
        case 'platform':
          next.platformProductIds.push(productId);
          break;
        case 'seat-pack':
          next.seatPackProductIds.push(productId);
          break;
        case 'first-five':
          next.firstFiveProductId = productId;
          break;
        case 'archived':
          next.archivedProductIds.push(productId);
          break;
        default: // uncategorized — remove from all
          break;
      }

      return next;
    });
    setCategoriesDirty(true);
  };

  const loadPriceCounts = async () => {
    const orgsRef = collection(db, 'organizations');
    const orgQuery = query(
      orgsRef,
      where('license.status', 'in', ['active', 'trialing'])
    );
    const snapshot = await getDocs(orgQuery);
    const counts = {};

    snapshot.forEach((doc) => {
      const data = doc.data();
      const license = data.license || {};
      const platformPriceId = license.platformPriceId;
      const seatPackPriceId = license.seatPackPriceId;

      if (platformPriceId) {
        counts[platformPriceId] = (counts[platformPriceId] || 0) + 1;
      }
      if (seatPackPriceId) {
        counts[seatPackPriceId] = (counts[seatPackPriceId] || 0) + 1;
      }
    });

    setPriceCounts(counts);
  };

  const loadStripeData = async () => {
    setLoading(true);
    setError('');
    setStripeStatus('checking');
    try {
      const functions = getFunctions();
      const listProducts = httpsCallable(functions, 'listStripeProducts');
      const listCustomers = httpsCallable(functions, 'listStripeCustomersWithSpend');

      const [productsResult, customersResult] = await Promise.all([
        listProducts({ limit: 100 }),
        listCustomers({ limit: 50 }),
        loadPriceCounts(),
      ]);

      setProducts(productsResult.data?.products || []);
      setCustomers(customersResult.data?.customers || []);
      setStripeStatus('connected');
    } catch (err) {
      console.error('Error loading Stripe data:', err);
      setError('Failed to load Stripe data. Please try again.');
      setStripeStatus('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin()) return;
    loadStripeData();
    loadCategories();
  }, [loadCategories]);

  const productUsage = useMemo(() => {
    return products.map((product) => {
      const priceUsage = (product.prices || []).map((price) => ({
        ...price,
        orgCount: priceCounts[price.id] || 0,
      }));
      const totalOrgs = priceUsage.reduce((sum, price) => sum + price.orgCount, 0);
      return { ...product, priceUsage, totalOrgs };
    });
  }, [products, priceCounts]);

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCreateProduct = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    try {
      const functions = getFunctions();
      const createProduct = httpsCallable(functions, 'createStripeProduct');
      const unitAmount = Math.round(parseFloat(formData.unitAmount) * 100);

      await createProduct({
        name: formData.name,
        description: formData.description || undefined,
        metadata: {
          productType: formData.productType,
        },
        price: {
          unitAmount,
          currency: formData.currency,
          interval: formData.interval,
          intervalCount: parseInt(formData.intervalCount, 10) || 1,
        },
      });

      setFormData({
        name: '',
        description: '',
        unitAmount: '',
        currency: 'usd',
        interval: 'month',
        intervalCount: '1',
        productType: 'seat-pack',
      });

      await loadStripeData();
    } catch (err) {
      console.error('Error creating Stripe product:', err);
      setError('Failed to create product. Please check the form and try again.');
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold mb-2">Products Management</h1>
          <p className="text-gray-600">
            Manage Stripe products, pricing, and see subscription usage by product.
          </p>
        </div>
        <button
          onClick={loadStripeData}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Refresh Data
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm">
        <a
          href="https://dashboard.stripe.com"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          Open Stripe Dashboard
          <span aria-hidden>↗</span>
        </a>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full font-medium ${
            stripeStatus === 'connected'
              ? 'bg-green-100 text-green-800'
              : stripeStatus === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          {stripeStatus === 'connected'
            ? 'Stripe Connected'
            : stripeStatus === 'error'
              ? 'Stripe Error'
              : 'Checking Stripe...'}
        </span>
        {stripeStatus === 'error' && (
          <span className="text-gray-500">Check function logs or credentials.</span>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg p-8 shadow">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg p-6 shadow">
            <h2 className="text-xl font-semibold mb-4">Stripe Products</h2>
            <div className="space-y-4">
              {productUsage.length === 0 ? (
                <p className="text-gray-500">No products found in Stripe.</p>
              ) : (
                productUsage.map((product) => (
                  <div key={product.id} className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold">{product.name}</h3>
                        <p className="text-sm text-gray-500">{product.description || 'No description'}</p>
                        <p className="text-xs text-gray-400 mt-1">Product ID: {product.id}</p>
                      </div>
                      <div className="text-sm font-medium text-blue-700">
                        Orgs on this product: {product.totalOrgs}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {product.priceUsage.length === 0 ? (
                        <p className="text-sm text-gray-500">No prices found for this product.</p>
                      ) : (
                        product.priceUsage.map((price) => (
                          <div key={price.id} className="bg-gray-50 border rounded-md p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Price ID</span>
                              <span className="text-xs text-gray-400">{price.id}</span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700">
                              {price.unitAmount !== null ? (
                                <span>
                                  {formatCurrency(price.unitAmount, price.currency)}{' '}
                                  {price.recurring?.interval ? `/ ${price.recurring.interval}` : ''}
                                </span>
                              ) : (
                                <span>Custom pricing</span>
                              )}
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              Orgs using this price: {price.orgCount}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Product Categorization ── */}
          <div className="bg-white rounded-lg p-6 shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Product Categories</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Assign each Stripe product to a category. The subscription modal uses these to display the right products.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={syncExistingOrgs}
                  disabled={syncing || categoriesDirty}
                  className="px-4 py-2 rounded-md font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title={categoriesDirty ? 'Save changes first before syncing' : 'Update existing orgs to match configured seat counts'}
                >
                  {syncing ? 'Syncing...' : 'Sync Existing Orgs'}
                </button>
                <button
                  onClick={saveCategories}
                  disabled={!categoriesDirty || savingCategories}
                  className={`px-4 py-2 rounded-md font-medium text-sm ${
                    categoriesDirty
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {savingCategories ? 'Saving...' : categoriesDirty ? 'Save Changes' : 'Saved'}
                </button>
              </div>
            </div>

            {/* Sync results */}
            {syncResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                syncResult.error
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : syncResult.updated > 0
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-blue-50 border border-blue-200 text-blue-700'
              }`}>
                {syncResult.error ? (
                  <p>Sync error: {syncResult.error}</p>
                ) : (
                  <>
                    <p className="font-medium">{syncResult.message}</p>
                    {syncResult.results?.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {syncResult.results.map((r, i) => (
                          <li key={i}>
                            {r.orgName}: {r.oldSeats} → <strong>{r.newSeats}</strong> seats
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Category columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Platform Products */}
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 min-h-[120px]">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-xs">P</span>
                  Platform Tiers
                </h3>
                {categories.platformProductIds.length === 0 ? (
                  <p className="text-sm text-blue-400 italic">No products assigned</p>
                ) : (
                  <div className="space-y-2">
                    {categories.platformProductIds.map(pid => {
                      const prod = products.find(p => p.id === pid);
                      return (
                        <div key={pid} className="bg-white rounded px-3 py-2 text-sm flex items-center justify-between border border-blue-200">
                          <span className="font-medium text-gray-800">{prod?.name || pid}</span>
                          <button onClick={() => setProductCategory(pid, 'uncategorized')} className="text-gray-400 hover:text-red-600 text-lg leading-none" title="Remove">×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Seat Pack Products */}
              <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50 min-h-[120px]">
                <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded bg-orange-600 text-white flex items-center justify-center text-xs">S</span>
                  Seat Packs
                </h3>
                {categories.seatPackProductIds.length === 0 ? (
                  <p className="text-sm text-orange-400 italic">No products assigned</p>
                ) : (
                  <div className="space-y-2">
                    {categories.seatPackProductIds.map(pid => {
                      const prod = products.find(p => p.id === pid);
                      const seatCount = categories.seatCounts[pid] || '';
                      return (
                        <div key={pid} className="bg-white rounded px-3 py-2 text-sm border border-orange-200">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-800">{prod?.name || pid}</span>
                            <button onClick={() => setProductCategory(pid, 'uncategorized')} className="text-gray-400 hover:text-red-600 text-lg leading-none" title="Remove">×</button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <label className="text-xs text-orange-700 font-medium whitespace-nowrap">Licenses:</label>
                            <input
                              type="number"
                              min="1"
                              value={seatCount}
                              onChange={(e) => setSeatCount(pid, e.target.value)}
                              placeholder="e.g. 25"
                              className="w-20 px-2 py-1 text-xs border border-orange-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                            />
                            {!seatCount && (
                              <span className="text-xs text-red-500">Required</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* First Five + Archived */}
              <div className="space-y-4">
                <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50 min-h-[60px]">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-green-600 text-white flex items-center justify-center text-xs">5</span>
                    First Five (Free)
                  </h3>
                  {!categories.firstFiveProductId ? (
                    <p className="text-sm text-green-400 italic">Not assigned</p>
                  ) : (
                    <div className="bg-white rounded px-3 py-2 text-sm flex items-center justify-between border border-green-200">
                      <span className="font-medium text-gray-800">{products.find(p => p.id === categories.firstFiveProductId)?.name || categories.firstFiveProductId}</span>
                      <button onClick={() => setProductCategory(categories.firstFiveProductId, 'uncategorized')} className="text-gray-400 hover:text-red-600 text-lg leading-none" title="Remove">×</button>
                    </div>
                  )}
                </div>
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[60px]">
                  <h3 className="font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-gray-500 text-white flex items-center justify-center text-xs">A</span>
                    Archived
                  </h3>
                  {categories.archivedProductIds.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">None</p>
                  ) : (
                    <div className="space-y-2">
                      {categories.archivedProductIds.map(pid => {
                        const prod = products.find(p => p.id === pid);
                        return (
                          <div key={pid} className="bg-white rounded px-3 py-2 text-sm flex items-center justify-between border border-gray-200 opacity-60">
                            <span className="font-medium text-gray-600">{prod?.name || pid}</span>
                            <button onClick={() => setProductCategory(pid, 'uncategorized')} className="text-gray-400 hover:text-red-600 text-lg leading-none" title="Remove">×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* All products with category assignment dropdown */}
            <h3 className="font-semibold text-gray-800 mb-3">All Stripe Products</h3>
            <div className="space-y-2">
              {products.map(product => {
                const cat = getProductCategory(product.id);
                const priceLabel = (product.prices || []).map(p =>
                  `${formatCurrency(p.unitAmount, p.currency)}/${p.recurring?.interval || '?'}`
                ).join(', ');

                return (
                  <div key={product.id} className={`flex items-center gap-4 border rounded-lg px-4 py-3 ${
                    cat === 'archived' ? 'bg-gray-50 opacity-60' : 'bg-white'
                  }`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{product.name}</div>
                      <div className="text-xs text-gray-500 truncate">{priceLabel || 'No prices'}</div>
                    </div>
                    {cat === 'seat-pack' && (
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-gray-500 whitespace-nowrap">Licenses:</label>
                        <input
                          type="number"
                          min="1"
                          value={categories.seatCounts[product.id] || ''}
                          onChange={(e) => setSeatCount(product.id, e.target.value)}
                          placeholder="#"
                          className="w-16 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-orange-500"
                        />
                      </div>
                    )}
                    <select
                      value={cat}
                      onChange={(e) => setProductCategory(product.id, e.target.value)}
                      className={`text-sm border rounded-md px-3 py-1.5 font-medium ${
                        cat === 'platform' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                        cat === 'seat-pack' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                        cat === 'first-five' ? 'bg-green-50 text-green-700 border-green-300' :
                        cat === 'archived' ? 'bg-gray-100 text-gray-500 border-gray-300' :
                        'bg-yellow-50 text-yellow-700 border-yellow-300'
                      }`}
                    >
                      <option value="uncategorized">-- Uncategorized --</option>
                      <option value="platform">Platform Tier</option>
                      <option value="seat-pack">Seat Pack</option>
                      <option value="first-five">First Five (Free)</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow">
            <h2 className="text-xl font-semibold mb-4">Create Stripe Product</h2>
            <form onSubmit={handleCreateProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.name}
                  onChange={handleFormChange('name')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.productType}
                  onChange={handleFormChange('productType')}
                >
                  <option value="platform">Platform</option>
                  <option value="seat-pack">Seat Pack</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2"
                  rows="2"
                  value={formData.description}
                  onChange={handleFormChange('description')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.unitAmount}
                  onChange={handleFormChange('unitAmount')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.currency}
                  onChange={handleFormChange('currency')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Interval</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.interval}
                  onChange={handleFormChange('interval')}
                >
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interval Count</label>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.intervalCount}
                  onChange={handleFormChange('intervalCount')}
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg p-6 shadow">
            <h2 className="text-xl font-semibold mb-4">Stripe Customers</h2>
            {customers.length === 0 ? (
              <p className="text-gray-500">No customers found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Customer</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Total Spent</th>
                      <th className="py-2 pr-4">Customer ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id} className="border-b last:border-b-0">
                        <td className="py-2 pr-4 font-medium text-gray-800">
                          {customer.name || 'Unnamed'}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">
                          {customer.email || 'N/A'}
                        </td>
                        <td className="py-2 pr-4 text-gray-700">
                          {formatCurrency(customer.totalPaid, customer.currency)}
                        </td>
                        <td className="py-2 pr-4 text-xs text-gray-400">
                          {customer.id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductsManagement;
